<template>
  <div class="feedback-wrapper">
    <div class="feedback">
      <div class="feedback-header">
        <div
          class="back-button-container"
          :class="{ hideFeedbackTitle: isMobile && isFeedbackProvided }"
          @click="returnToPreviousTab"
        >
          <v-icon class="back-button">
            chevron_left
          </v-icon>
          <span
            v-if="activeTabIndex === 0"
            @click.stop="$emit('close')"
          >FEEDBACK</span>
          <span v-else>{{ tabs[activeTabIndex - 1] }}</span>
        </div>
        <div v-if="propertyData" class="address-image">
          <div class="img-wrap">
            <v-img
              class="home-image"
              :aspect-ratio="16 / 10"
              :src="getImageSrc(propertyData)"
            />
          </div>
          <h2>
            {{ propertyData["Address"] }} {{ propertyData["City"] }}
            {{ propertyData["PostalCode"] }}
          </h2>
        </div>
        <div v-if="isFeedbackProvided" class="feedback-circle-container">
          <feedback-pref-circle
            class="feedback-circle"
            :pref-data="fullPrefData"
            :colors="colors"
          />
        </div>
        <v-icon class="feedback-modal__close" @click.stop="$emit('close')">
          close
        </v-icon>
      </div>
      <div class="feedback-body">
        <ul class="feedback-tabs">
          <li
            v-for="(item, index) in tabs"
            :key="index"
            :class="[
              index === activeTabIndex && !isFeedbackProvided ? 'active' : '',
              isFeedbackProvided ? 'overviewTabs' : ''
            ]"
            @click="openTab(index)"
          >
            <span
              :class="{
                filled:
                  isTabFilled(index, activeTabIndex) ||
                  isFeedbackProvided ||
                  editingState
              }"
            >
              <v-icon>
                fa fa-check
              </v-icon>
            </span>
            {{ item }}
          </li>
        </ul>
        <feedback-overview
          v-if="isFeedbackProvided"
          :feedback-data="feedbackData"
        />
        <feedback-update
          v-else
          ref="feedbackUpdateComponent"
          :feedback-data="feedbackData"
          :active-tab-index="activeTabIndex"
          @on-update="updateFeedbackData"
        />
      </div>
      <div v-if="!isFeedbackProvided" class="feedback-footer">
        <button v-if="activeTabIndex < 3" class="hw-btn" @click="openNextTab">
          {{ buttonTitle }}
        </button>
        <button v-else class="hw-btn" @click="triggerSubmit">
          Save
        </button>
      </div>
      <div v-else class="feedback-footer">
        <button class="hw-btn" @click="editingState = true">
          UPDATE FEEDBACK
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import FeedbackUpdate from './FeedbackUpdate'
import FeedbackOverview from './FeedbackOverview'
import FeedbackPrefCircle from '../common/feedback-modal/FeedbackPrefCircle.vue'
import colors from '../../scss/_colors.scss'

import { saveFeedback } from '../../services/feedbackCalculationService'
import { isEmpty } from '../../utils/generalUtils'

import {
  feedbackProvidedProperty,
  getPropertyImage
} from '../../utils/propertyUtils'

import refreshClientData from '../mixins/refreshClientData'

export default {
  components: {
    FeedbackUpdate,
    FeedbackOverview,
    FeedbackPrefCircle
  },
  mixins: [refreshClientData],
  props: {
    feedbackData: {
      type: Object,
      required: true
    },
    propertyData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      activeTabIndex: 0,
      tabs: ['Needs', 'Wants', 'Notes', 'Pics'],
      editingState: false,
      colors,
      filledTabs: new Set()
    }
  },

  computed: {
    ...mapState('client', ['clientData']),
    ...mapState('common', ['isMobile']),
    ...mapState('feedback', ['feedbackNote']),
    ...mapGetters('client', ['clientPreferences']),

    fullPrefData() {
      const match =
        this.wantNeedData.filter(item => item.wantNeed === 'Want' && item.match)
          .length +
        this.wantNeedData.filter(item => item.wantNeed === 'Need' && item.match)
          .length
      const total =
        this.wantNeedData.filter(item => item.wantNeed === 'Want').length +
        this.wantNeedData.filter(item => item.wantNeed === 'Need').length

      return {
        data: [
          ...this.wantNeedData.filter(item => item.wantNeed === 'Want'),
          ...this.wantNeedData.filter(item => item.wantNeed === 'Need')
        ],
        match,
        total,
        color: this.getPercentColor(match, total)
      }
    },

    wantNeedData() {
      return this.feedbackData.feedback.match.results
    },
    isFeedbackProvided() {
      return !this.editingState
        ? !!feedbackProvidedProperty(this.feedbackData)
        : false
    },
    buttonTitle() {
      switch (this.activeTabIndex) {
        case 0:
          return 'Continue to Wants'
        case 1:
          return 'Continue to Notes'
        case 2:
          return 'Continue to PICS'
        default:
          return ''
      }
    }
  },

  methods: {
    ...mapMutations('client', {
      setClientData: 'SET_CLIENT_DATA'
    }),
    ...mapMutations('feedback', {
      setSaveNote: 'SET_SAVE_NOTE'
    }),

    getPercentColor(match, total) {
      if (!match && !total) {
        return colors.colorGray
      }
      const percent = Math.round((match / total) * 100)
      if (percent >= 80) return colors.scheduled
      return percent >= 60 ? colors.hwayzSun : colors.colorGray
    },

    getImageSrc(item) {
      if (item['Images']) {
        return getPropertyImage(item)
      }
    },

    triggerSubmit() {
      this.$refs.feedbackUpdateComponent.submit()
    },

    async updateFeedbackData(data) {
      this.saveInProgress = true

      const response = (await saveFeedback(
        this.clientData._id,
        this.feedbackData._id,
        data
      )).data
      this.editingState = false
      this.activeTabIndex = 0
      this.refreshData(response)
      this.$emit('on-feedback-updated', response)
    },

    refreshData(response) {
      if (this.profileStatus === 'agent') {
        const clientHomes = this.clientData.homes
        const propertyIndex = this.clientData.homes.findIndex(
          home => home.propertyId === response.propertyId
        )
        if (propertyIndex >= 0) {
          clientHomes[propertyIndex] = response
          this.setClientData({
            ...this.clientData,
            homes: [...clientHomes]
          })
        }
      } else this.getClientData()
    },

    openNextTab() {
      if (this.activeTabIndex === 2 && !isEmpty(this.feedbackNote)) {
        this.setSaveNote(true)
      }
      this.filledTabs.add(this.activeTabIndex)
      this.activeTabIndex += 1
    },

    returnToPreviousTab() {
      this.activeTabIndex -= 1
    },
    isTabFilled(index, activeTabIndex) {
      return this.filledTabs.has(index)
    },
    openTab(tab) {
      this.editingState = true
      this.filledTabs.add(this.activeTabIndex)
      this.activeTabIndex = tab
      this.filledTabs.add(tab)
    }
  }
}
</script>

<style lang="sass">
.feedback-wrapper
  background: #fff
.feedback
  min-height: calc(100vh - 100px)
  display: flex
  flex-wrap: wrap
  flex-flow: column
.feedback-footer
  display: flex
  justify-content: center
  align-items: flex-end
  padding-bottom: 4rem
.feedback-header
  display: flex
  width: 100%
  height: 88px
  justify-content: space-between
  align-items: center
  padding: 0px 25px
  border: 1px solid #F1F1F1
  position: relative
  .img-wrap
    width: 134px
    min-width: 134px
    max-width: 134px
    height: 69px
    @media only screen and (max-width: 1024px)
      width: 110px
      min-width: 110px
      max-width: 110px
      height: 60px
    .home-image
      height: 100%
  h2
    margin-bottom: 0
    font-size: 20px
    line-height: 36px
    text-transform: capitalize
    color: #4A4949
    font-weight: 400
    @media only screen and (max-width: 1024px)
      font-size: 16px
      line-height: 24px
.feedback-header .address-image
  position: absolute
  max-width: 50%
  top: 1
  left: 35%
  @media only screen and (max-width: 1024px)
    left: 25%
.back-button-container
  display: flex
  cursor: pointer
  align-items: center
  position: absolute
  width: 125px
.back-button-container span
  font-weight: 500
  color: $black !important
  text-transform: uppercase
.back-button
  vertical-align: middle
  padding-right: 6px
  cursor: pointer
.feedback-modal__close
  position: absolute
  top: 25px
  right: 15px
.feedback-modal__close, .back-button
  font-size: 35px
  color: $black !important
.feedback-body
  padding: 25px 25px
  flex: 1
.feedback-tabs
  display: flex
  max-width: 600px
  margin: 0 auto 30px !important
  justify-content: space-between
  li
    text-transform: uppercase
    font-weight: 400
    color: $color-grey
    padding: 0 14px 18px !important
    display: flex
    align-items: center
    cursor: pointer
    border-bottom: 5px solid transparent
    transition: all 0.3s
    &:hover
      color: $hwayzBrandGold
    span
      display: block
      width: 16px
      height: 16px
      background: gray
      border-radius: 50%
      margin-right: 10px
      .v-icon
        font-size: 11px
        color: $hwayzWhite
        padding: 2px 2px 8px
      &.filled
        background: green !important
    &.active
      border-bottom: 5px solid $hwayzBrandGold
      color: $hwayzBrandGold
    &.overviewTabs
      color: $hwayzBrandGold
.feedback-items
  margin-bottom: 40px
.mobile-feedback-component
  position: fixed
  z-index: 10
  top: 47px
  left: 0
  height: calc(100vh - 47px)
  width: 100%
  overflow: scroll
  .feedback-tabs
    display: none
  .feedback-body
    padding: 20px 15px
  .feedback-modal__close
    display: none
  .feedback-header
    padding: 0 15px
    justify-content: space-around
  .address-image
    position: static
    h2
      font-size: 16px !important
    .img-wrap
      display: none
  .overview-container
    flex-wrap: wrap
  .overview-component-container
    margin-bottom: 20px
    width: 100%
  .feedback-chips-header
    flex-wrap: wrap
  .feedback-circle
    display: flex
    flex-direction: row
  .feedback-circle-container .v-progress-circular
    width: 30px !important
    height: 30px !important
  .feedback-circle-container .pref-percent
    margin-left: 5px
  .feedback-circle-container .pref-percent h3
    font-size: 14px
    line-height: 1
  .feedback-circle-container .pref-percent span
    display: none
  .feedback-circle-container
    position: static
  .back-button-container
    position: static
.feedback-circle-container
  padding: 0 10px
  position: absolute
  top: 15px
  right: 50px
  @media only screen and (max-width: 768px)
    right: 20px
.feedback-circle-container .v-progress-circular
  width: 58px !important
  height: 58px !important
  @media only screen and (max-width: 1024px)
    width: 48px !important
    height: 48px !important
.feedback-circle-container .pref-percent h3
  font-size: 24px
  line-height: 1
  @media only screen and (max-width: 1024px)
    font-size: 18px
.feedback
  @media only screen and (max-width: 768px)
    .feedback-tabs
      display: none
    .feedback-body
      padding: 20px 15px
    .feedback-modal__close
      display: none
    .feedback-header
      padding: 0 15px
    .address-image
      h2
        font-size: 18px
      .img-wrap
        width: 100px
        min-width: 100px
        max-width: 100px
        height: 50px
    .overview-container
      flex-wrap: wrap
    .overview-component-container
      margin-bottom: 20px
      width: 100%
    .feedback-chips-header
      flex-wrap: wrap
    .feedback-circle
      display: flex
      flex-direction: row
    .feedback-circle-container
      top: 20px
    .feedback-circle-container .v-progress-circular
      width: 40px !important
      height: 40px !important
    .feedback-circle-container .pref-percent
      margin-left: 10px
    .feedback-circle-container .pref-percent h3
      font-size: 20px
      line-height: 1
    .feedback-circle-container .pref-percent span
      display: none
.hideFeedbackTitle
  display: none
</style>
